import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  FaPlus,
  FaEdit,
  FaBriefcase,
  FaArrowLeft,
  FaStar,
  FaSearch,
  FaSpinner,
  FaTrash,
  FaExclamationTriangle,
  FaLink,
  FaListUl,
} from "react-icons/fa";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";
import useApp from "../../hooks/useAppContext";
import { useNavigate, Link } from "react-router-dom";
import useAuth from "../../hooks/useAuthContext";
import api from "../../services/axios";
import ModalTodasEscolhasPatrocinadores from "../../components/modal/modalTodasEscolhasPatrocinadores";
import "./admPatrocinadoresList.css";

function idsReunioesNegocio(usuario) {
  if (!usuario?.agendaParticipante?.length) return [];
  const ids = usuario.agendaParticipante
    .filter((ap) => ap.agenda?.tipo === "reuniao_negocio")
    .map((ap) => ap.id_agenda);
  return [...new Set(ids)];
}

function escolhasResumo(escolhaPatrocinador) {
  if (!escolhaPatrocinador?.length) {
    return { maisQuero: 0, quero: 0, total: 0 };
  }
  const maisQuero = escolhaPatrocinador.filter(
    (e) => e.tipo === "mais_quero_conversar" || e.tipo === "mais_quero"
  ).length;
  const quero = escolhaPatrocinador.filter(
    (e) => e.tipo === "quero_conversar" || e.tipo === "quero"
  ).length;
  return { maisQuero, quero, total: escolhaPatrocinador.length };
}

function qtdParticipantesPatrocinador(p) {
  return (
    p?.qtd_participantes_credenciados ??
    p?.qtdParticipantesCredenciados ??
    0
  );
}

function semParticipantes(p) {
  return qtdParticipantesPatrocinador(p) < 1;
}

const SEM_PLANO = "Sem plano";

const AdmPatrocinadores = () => {
  const navigate = useNavigate();
  const { getPatrocinadoresLista, excluirPatrocinador } = useApp();
  const { usuario } = useAuth();

  const [patrocinadores, setPatrocinadores] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [excluindoId, setExcluindoId] = useState(null);
  const [patrocinadorParaExcluir, setPatrocinadorParaExcluir] = useState(null);
  const [error, setError] = useState(null);
  const [filtro, setFiltro] = useState("");
  const [filtroSimultaneas, setFiltroSimultaneas] = useState(true);
  const [filtroPlano, setFiltroPlano] = useState(null);
  const [filtroSemParticipantes, setFiltroSemParticipantes] = useState(false);
  const [gerandoLinksSelecao, setGerandoLinksSelecao] = useState(false);
  const [modalEscolhasAberto, setModalEscolhasAberto] = useState(false);

  const carregarPatrocinadores = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const pat = await getPatrocinadoresLista();
      setPatrocinadores(pat);
      return pat;
    } catch (err) {
      console.error("Erro ao carregar patrocinadores:", err);
      setError("Erro ao carregar lista de patrocinadores");
    } finally {
      setIsLoading(false);
    }
  }, [getPatrocinadoresLista]);

  useEffect(() => {
    carregarPatrocinadores();
  }, [carregarPatrocinadores]);

  const abrirConfirmacaoExcluir = (p) => {
    if (!p?.id || excluindoId) return;
    setPatrocinadorParaExcluir(p);
  };

  const fecharConfirmacaoExcluir = () => {
    if (excluindoId) return;
    setPatrocinadorParaExcluir(null);
  };

  const confirmarExcluir = async () => {
    const p = patrocinadorParaExcluir;
    if (!p?.id || excluindoId) return;

    setExcluindoId(p.id);
    try {
      const resp = await excluirPatrocinador(p.id);
      if (resp?.mensagem) {
        toast.success(resp.mensagem);
        setPatrocinadores((atual) => atual.filter((item) => item.id !== p.id));
        setPatrocinadorParaExcluir(null);
      } else {
        toast.error(
          resp?.mensagem || "Não foi possível excluir o patrocinador."
        );
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.mensagem || "Erro ao excluir patrocinador."
      );
    } finally {
      setExcluindoId(null);
    }
  };

  const gerarLinksSelecaoExcel = useCallback(async () => {
    if (gerandoLinksSelecao) return;
    setGerandoLinksSelecao(true);
    try {
      const resp = await api.get("/patrocinador-links-selecao-excel", {
        responseType: "blob",
      });

      const disposition = resp.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/i.exec(disposition);
      const fileName =
        match?.[1] ||
        `patrocinadores_links_selecao_${new Date().toISOString().slice(0, 10)}.xlsx`;

      const blob = new Blob([resp.data], {
        type:
          resp.headers?.["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, fileName);

      const gerados = resp.headers?.["x-links-gerados"];
      const reutilizados = resp.headers?.["x-links-reutilizados"];
      if (gerados != null || reutilizados != null) {
        toast.success(
          `Excel baixado. Links novos: ${gerados ?? 0}. Reutilizados: ${reutilizados ?? 0}.`
        );
      } else {
        toast.success("Excel com links de seleção baixado.");
      }
    } catch (err) {
      console.error("Erro ao gerar links de seleção:", err);
      let mensagem = "Erro ao gerar Excel com links de seleção.";
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          const texto = await data.text();
          const json = JSON.parse(texto);
          mensagem = json?.mensagem || mensagem;
        } catch {
          /* mantém mensagem padrão */
        }
      } else if (data?.mensagem) {
        mensagem = data.mensagem;
      }
      toast.error(mensagem);
    } finally {
      setGerandoLinksSelecao(false);
    }
  }, [gerandoLinksSelecao]);

  const patrocinadoresFiltrados = useMemo(() => {
    let resultado = patrocinadores;
    if (filtro.trim()) {
      const termo = filtro.toLowerCase().trim();
      resultado = resultado.filter((p) =>
        (p.patrocinador || "").toLowerCase().includes(termo)
      );
    }
    if (filtroSimultaneas) {
      resultado = resultado.filter(
        (p) => (p?.plano?.cota_simultania || 0) >= 1
      );
    }
    if (filtroPlano) {
      resultado = resultado.filter((p) => {
        const nomePlano = p?.plano?.nome?.trim();
        if (filtroPlano === SEM_PLANO) return !nomePlano;
        return nomePlano === filtroPlano;
      });
    }
    if (filtroSemParticipantes) {
      resultado = resultado.filter(semParticipantes);
    }
    return resultado;
  }, [patrocinadores, filtro, filtroSimultaneas, filtroPlano, filtroSemParticipantes]);

  const indicadores = useMemo(() => {
    const porPlanoMap = new Map();
    let semPlano = 0;
    let semParticipantesCount = 0;

    patrocinadores.forEach((p) => {
      if (semParticipantes(p)) {
        semParticipantesCount += 1;
      }

      const nomePlano = p?.plano?.nome?.trim();
      if (!nomePlano) {
        semPlano += 1;
        return;
      }
      porPlanoMap.set(nomePlano, (porPlanoMap.get(nomePlano) || 0) + 1);
    });

    const porPlano = [...porPlanoMap.entries()]
      .map(([nome, quantidade]) => ({ nome, quantidade }))
      .sort(
        (a, b) =>
          b.quantidade - a.quantidade || a.nome.localeCompare(b.nome, "pt-BR")
      );

    if (semPlano > 0) {
      porPlano.push({ nome: SEM_PLANO, quantidade: semPlano });
    }

    return {
      total: patrocinadores.length,
      porPlano,
      semParticipantes: semParticipantesCount,
    };
  }, [patrocinadores]);

  const selecionarFiltroPlano = useCallback((nomePlano) => {
    setFiltroPlano((atual) => (atual === nomePlano ? null : nomePlano));
  }, []);

  const limparFiltroPlano = useCallback(() => {
    setFiltroPlano(null);
  }, []);

  const alternarFiltroSemParticipantes = useCallback(() => {
    setFiltroSemParticipantes((atual) => !atual);
  }, []);

  const isAdmin = usuario?.tipo === "administracao";
  const estaExcluindo = !!excluindoId;
  const nomeExclusao =
    patrocinadorParaExcluir?.patrocinador?.trim() || "sem nome";
  const planoExclusao =
    patrocinadorParaExcluir?.plano?.nome?.trim() || null;

  return (
    <div className="container-adm-main">
      <div className="apl-page">
        <Modal
          show={!!patrocinadorParaExcluir}
          onHide={fecharConfirmacaoExcluir}
          centered
          className="apl-excluir-patrocinador-modal"
          backdrop={estaExcluindo ? "static" : true}
        >
          <Modal.Header closeButton={!estaExcluindo}>
            <Modal.Title className="apl-excluir-patrocinador-modal-title">
              <FaExclamationTriangle
                size={18}
                className="apl-excluir-patrocinador-modal-icon"
                aria-hidden
              />
              Confirmar exclusão
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="apl-excluir-patrocinador-modal-text mb-0">
              Você está prestes a excluir o patrocinador{" "}
              <strong>{nomeExclusao}</strong>
              {planoExclusao ? (
                <>
                  {" "}
                  do plano <strong>{planoExclusao}</strong>
                </>
              ) : null}
              .
            </p>
            <p className="apl-excluir-patrocinador-modal-text apl-excluir-patrocinador-modal-text--danger mb-0 mt-3">
              Esta ação é <strong>definitiva</strong> na interface administrativa.
              Os usuários vinculados e as agendas relacionadas também serão
              removidos.
            </p>
            <p className="apl-excluir-patrocinador-modal-text mb-0 mt-3">
              Deseja confirmar a exclusão de <strong>{nomeExclusao}</strong>?
            </p>
          </Modal.Body>
          <Modal.Footer className="apl-excluir-patrocinador-modal-footer">
            <button
              type="button"
              className="apl-btn apl-btn--ghost"
              onClick={fecharConfirmacaoExcluir}
              disabled={estaExcluindo}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="apl-btn apl-btn--danger-solid"
              onClick={confirmarExcluir}
              disabled={estaExcluindo}
            >
              {estaExcluindo ? (
                <FaSpinner className="fa-spin" size={14} aria-hidden />
              ) : (
                <FaTrash size={14} aria-hidden />
              )}
              {estaExcluindo ? "Excluindo…" : "Excluir definitivamente"}
            </button>
          </Modal.Footer>
        </Modal>

        <ModalTodasEscolhasPatrocinadores
          show={modalEscolhasAberto}
          onHide={() => setModalEscolhasAberto(false)}
        />

        <header className="apl-header" aria-label="Cabeçalho da listagem">
          <div className="apl-header-text">
            <h1>Patrocinadores</h1>
            <p>
              Visualize cotas, marcações e acesse o cadastro ou as reuniões de
              cada patrocinador.
            </p>
          </div>

          <div className="apl-toolbar">
            <div className="apl-search">
              <span className="apl-search-icon" aria-hidden>
                <FaSearch size={14} />
              </span>
              <input
                type="search"
                className="apl-search-input"
                placeholder="Buscar por nome…"
                value={filtro}
                onChange={(e) => setFiltro(e.target.value)}
                disabled={isLoading}
                aria-label="Buscar patrocinador por nome"
              />
            </div>

            <label className="apl-filter-pill">
              <input
                type="checkbox"
                checked={filtroSimultaneas}
                onChange={(e) => setFiltroSimultaneas(e.target.checked)}
                disabled={isLoading}
              />
              Com marcação
            </label>

            {isAdmin && (
              <button
                type="button"
                className="apl-btn apl-btn--secondary"
                onClick={() => setModalEscolhasAberto(true)}
                disabled={isLoading}
                title="Lista todas as escolhas dos patrocinadores com download"
              >
                <FaListUl size={14} aria-hidden />
                Escolhas
              </button>
            )}

            {isAdmin && (
              <button
                type="button"
                className="apl-btn apl-btn--secondary"
                onClick={gerarLinksSelecaoExcel}
                disabled={isLoading || gerandoLinksSelecao}
                title="Gera links só para quem tem marcação (cota de simultaneidade) e baixa Excel"
              >
                {gerandoLinksSelecao ? (
                  <FaSpinner className="fa-spin" size={14} aria-hidden />
                ) : (
                  <FaLink size={14} aria-hidden />
                )}
                {gerandoLinksSelecao ? "Gerando links…" : "Links de seleção"}
              </button>
            )}

            {isAdmin && (
              <Link
                to="/private/adm/portal-patrocinador"
                className="apl-btn apl-btn--secondary"
              >
                <FaStar size={14} aria-hidden />
                Portal
              </Link>
            )}

            {isAdmin && (
              <Link
                to="/private/adm/patrocinadores/novo"
                className="apl-btn apl-btn--primary"
              >
                <FaPlus size={14} aria-hidden />
                Novo patrocinador
              </Link>
            )}

            <button
              type="button"
              className="apl-btn apl-btn--ghost"
              onClick={() => navigate(-1)}
              title="Voltar"
            >
              <FaArrowLeft size={14} aria-hidden />
              Voltar
            </button>
          </div>
        </header>

        <div className="body-tela-adm" style={{ paddingBottom: 0 }}>
          {error ? <div className="apl-alert" role="alert">{error}</div> : null}

          {!isLoading && patrocinadores.length > 0 ? (
            <section
              className="apl-metrics"
              aria-label="Indicadores de patrocinadores"
            >
              <button
                type="button"
                className={`apl-metric apl-metric--total${
                  filtroPlano === null && !filtroSemParticipantes
                    ? " is-active"
                    : ""
                }`}
                onClick={() => {
                  limparFiltroPlano();
                  setFiltroSemParticipantes(false);
                }}
                aria-pressed={filtroPlano === null && !filtroSemParticipantes}
                title="Mostrar todos os planos"
              >
                <span className="apl-metric-value">{indicadores.total}</span>
                <span className="apl-metric-label">
                  {indicadores.total === 1
                    ? "Patrocinador"
                    : "Patrocinadores"}
                </span>
              </button>
              {indicadores.porPlano.map((plano) => {
                const ativo = filtroPlano === plano.nome;
                return (
                  <button
                    key={plano.nome}
                    type="button"
                    className={`apl-metric${ativo ? " is-active" : ""}`}
                    onClick={() => selecionarFiltroPlano(plano.nome)}
                    aria-pressed={ativo}
                    title={`Filtrar por ${plano.nome}`}
                  >
                    <span className="apl-metric-value">{plano.quantidade}</span>
                    <span className="apl-metric-label" title={plano.nome}>
                      {plano.nome}
                    </span>
                  </button>
                );
              })}
              <button
                type="button"
                className={`apl-metric apl-metric--alert${
                  filtroSemParticipantes ? " is-active" : ""
                }`}
                onClick={alternarFiltroSemParticipantes}
                aria-pressed={filtroSemParticipantes}
                title="Filtrar patrocinadores sem participantes cadastrados"
              >
                <span className="apl-metric-value">
                  {indicadores.semParticipantes}
                </span>
                <span className="apl-metric-label">
                  {indicadores.semParticipantes === 1
                    ? "Sem participante"
                    : "Sem participantes"}
                </span>
              </button>
            </section>
          ) : null}

          {isLoading ? (
            <div className="apl-card">
              <div className="apl-loading">
                <FaSpinner className="fa-spin" size={28} aria-hidden />
                <span>Carregando patrocinadores…</span>
              </div>
            </div>
          ) : (
            <section className="apl-card" aria-label="Tabela de patrocinadores">
              <div className="apl-table-wrap">
                {patrocinadoresFiltrados.length === 0 ? (
                  <div className="apl-empty">
                    <p className="apl-empty-title">
                      {patrocinadores.length === 0
                        ? "Nenhum patrocinador cadastrado"
                        : "Nenhum resultado"}
                    </p>
                    <p style={{ margin: 0 }}>
                      {patrocinadores.length === 0
                        ? "Cadastre um patrocinador para vê-lo nesta listagem."
                        : "Ajuste a busca, o filtro de plano, sem participantes ou \"Com marcação\" para ver patrocinadores."}
                    </p>
                  </div>
                ) : (
                  <table className="apl-table">
                    <thead>
                      <tr>
                        <th className="apl-cell-logo" scope="col">
                          Logo
                        </th>
                        <th scope="col">Patrocinador</th>
                        <th scope="col">Plano</th>
                        <th scope="col">Reuniões</th>
                        <th scope="col">Simult.</th>
                        <th scope="col">Participantes</th>
                        <th scope="col">Escolhas</th>
                        <th scope="col" style={{ textAlign: "right" }}>
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {patrocinadoresFiltrados.map((p) => {
                        const reunioesIds = idsReunioesNegocio(p.usuario);
                        const cotaReun = p?.plano?.cota_reunioes ?? 0;
                        const { maisQuero, quero, total } = escolhasResumo(
                          p?.escolhaPatrocinador
                        );
                        const escolhasLabel =
                          maisQuero > 0 || quero > 0
                            ? `${maisQuero} · ${quero}`
                            : String(total);
                        const escolhasTitle =
                          maisQuero > 0 || quero > 0
                            ? `Prioritário: ${maisQuero} · Desejável: ${quero}`
                            : `Total de escolhas: ${total}`;

                        const qtdParticipantes = qtdParticipantesPatrocinador(p);
                        const estaExcluindo = excluindoId === p.id;

                        return (
                          <tr key={p.id}>
                            <td className="apl-cell-logo">
                              {p.logo ? (
                                <img
                                  className="apl-logo-thumb"
                                  src={p.logo}
                                  alt=""
                                />
                              ) : (
                                <div className="apl-logo-placeholder">—</div>
                              )}
                            </td>
                            <td className="apl-cell-name">{p.patrocinador}</td>
                            <td>
                              {p?.plano?.nome ? (
                                <span className="apl-tag-plan">
                                  {p.plano.nome}
                                </span>
                              ) : (
                                <span className="apl-stat-muted">—</span>
                              )}
                            </td>
                            <td>
                              <span className="apl-stat">
                                {reunioesIds.length}
                                <span className="apl-stat-muted">
                                  {" "}
                                  / {cotaReun}
                                </span>
                              </span>
                            </td>
                            <td>
                              <span className="apl-stat">
                                {p?.plano?.cota_simultania ?? "—"}
                              </span>
                            </td>
                            <td>
                              <span
                                className="apl-stat"
                                title="Participantes credenciados vinculados ao patrocinador"
                              >
                                {qtdParticipantes}
                              </span>
                            </td>
                            <td>
                              <span className="apl-stat" title={escolhasTitle}>
                                {escolhasLabel}
                              </span>
                            </td>
                            <td>
                              <div className="apl-actions">
                                {isAdmin && (
                                  <Link
                                    to={`/private/adm/patrocinadores/editar/${p.id}`}
                                  >
                                    <FaEdit size={12} aria-hidden />
                                    Editar
                                  </Link>
                                )}
                                {p.id_usuario && (
                                  <Link
                                    to={`/private/adm/convidados/reunioes/${p.id_usuario}`}
                                  >
                                    <FaBriefcase size={12} aria-hidden />
                                    Reuniões
                                  </Link>
                                )}
                                {isAdmin && (
                                  <button
                                    type="button"
                                    className="apl-action-btn apl-action-btn--danger"
                                    onClick={() => abrirConfirmacaoExcluir(p)}
                                    disabled={!!excluindoId}
                                    title="Excluir patrocinador"
                                  >
                                    {estaExcluindo ? (
                                      <FaSpinner
                                        className="fa-spin"
                                        size={12}
                                        aria-hidden
                                      />
                                    ) : (
                                      <FaTrash size={12} aria-hidden />
                                    )}
                                    Excluir
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {patrocinadoresFiltrados.length > 0 ? (
                <footer className="apl-footer">
                  <span>
                    Exibindo{" "}
                    <strong>{patrocinadoresFiltrados.length}</strong>{" "}
                    {patrocinadoresFiltrados.length === 1
                      ? "patrocinador"
                      : "patrocinadores"}
                    {(filtro ||
                      filtroSimultaneas ||
                      filtroPlano ||
                      filtroSemParticipantes) && (
                      <>
                        {" "}
                        <span className="apl-stat-muted">
                          (de {patrocinadores.length} no total)
                        </span>
                      </>
                    )}
                  </span>
                  <span>
                    {[
                      filtroPlano ? `Plano: ${filtroPlano}` : null,
                      filtroSemParticipantes ? "Sem participantes" : null,
                      filtroSimultaneas ? "Com marcação" : null,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "Todos os patrocinadores"}
                  </span>
                </footer>
              ) : null}
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdmPatrocinadores;
